import {registerStyle, styleRule, styleRuleInherit, StyleSheet, UI} from "../../../../stem-core/src/ui/UI";
import {FacebookIcon, GoogleIcon, LinkedInIcon, TwitterIcon} from "../../../SVGElements";
import {BaseButton, BaseButtonStyle} from "../../../ui/Button";
import {NOOP_FUNCTION} from "../../../../stem-core/src/base/Utils";
import {SOCIAL_APPS} from "../../../../blink-sdk/Constants";
import {isSmallScreen} from "../../../UtilsLib.js";
import {GetExternalAuthClient} from "../socialaccount/ExternalAuthConfigs.jsx";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../../../../blink-sdk/utils/AnalyticsClient.js";

class SocialButtonStyle extends BaseButtonStyle {
    @styleRuleInherit
    button = {
        position: "relative",
        boxShadow: this.themeProps.SOCIAL_BUTTON_SHADOW,
        display: "flex",
        alignItems: "center",
        fontWeight: () => this.themeProps.MERCHANT_FONT_WEIGHT_BOLD + " !important",
        padding: 0,
        width: () => (isSmallScreen() ? 44 : 48),
        height: () => (isSmallScreen() ? 44 : 48),
        borderRadius: 2,
        ":hover": {
            opacity: 1,
            transition: ".2s",
            boxShadow: this.themeProps.SOCIAL_BUTTON_HOVER_SHADOW,
        },
    };

    socialIconMobile = {
        margin: "0 auto",
    };

    @styleRule
    socialIconContainer = [
        {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            ">*": {
                transform: "scale(2)",
            },
            ">*>*": {
                display: "flex",
                alignItems: "center",
            },
        },
        this.socialIconMobile,
    ];
}

@registerStyle(SocialButtonStyle)
class SocialButton extends BaseButton {
    getIcon() {
        const IconClass = this.options.Icon;
        return <IconClass/>;
    }

    render() {
        const {description} = this.options;
        return [
            <div className={this.styleSheet.socialIconContainer}>
                {this.getIcon()}
            </div>,
            description,
        ];
    }

    startExternalAuth() {
        const {clientName} = this.options;

        dispatchAnalyticsEvent(AnalyticsEventType.SOCIAL_LOGIN_BUTTON_CLICKED, {provider: clientName})

        const externalAuthClient = GetExternalAuthClient(this.options.clientName);
        externalAuthClient.setLoginCallback(this.options.onLoginCallback);
        externalAuthClient.login();
    }

    onMount() {
        this.addClickListener(() => {
            this.startExternalAuth();
        });
    }
}

class FacebookButtonStyle extends SocialButtonStyle {
    @styleRuleInherit
    button = {
        color: this.themeProps.SOCIAL_BUTTON_FACEBOOK_COLOR,
        backgroundColor: this.themeProps.SOCIAL_BUTTON_FACEBOOK_BACKGROUND,
    };
}

class GoogleButtonStyle extends SocialButtonStyle {
    @styleRuleInherit
    button = {
        color: this.themeProps.SOCIAL_BUTTON_GOOGLE_COLOR,
        backgroundColor: this.themeProps.SOCIAL_BUTTON_GOOGLE_BACKGROUND,
    };
}

class LinkedInButtonStyle extends SocialButtonStyle {
    @styleRuleInherit
    button = {
        color: this.themeProps.SOCIAL_BUTTON_LINKEDIN_COLOR,
        backgroundColor: this.themeProps.SOCIAL_BUTTON_LINKEDIN_BACKGROUND,
        ">*": {
            transform: "translateY(-2px)",
        },
    };
}

class TwitterButtonStyle extends SocialButtonStyle {
    @styleRuleInherit
    button = {
        color: this.themeProps.SOCIAL_BUTTON_TWITTER_COLOR,
        backgroundColor: this.themeProps.SOCIAL_BUTTON_TWITTER_BACKGROUND,
    };
}


const socialAppDescriptors = {
    [SOCIAL_APPS.Google]: {
        icon: GoogleIcon,
        styleSheet: GoogleButtonStyle,
    },
    [SOCIAL_APPS.Facebook]: {
        icon: FacebookIcon,
        styleSheet: FacebookButtonStyle,
    },
    [SOCIAL_APPS.Twitter]: {
        icon: TwitterIcon,
        styleSheet: TwitterButtonStyle,
    },
    [SOCIAL_APPS.LinkedIn]: {
        icon: LinkedInIcon,
        styleSheet: LinkedInButtonStyle,
    },
};

class SocialLoginButtonsStyle extends StyleSheet {
    @styleRule
    buttons = {
        display: "flex",
        justifyContent: "space-around",
        maxWidth: 300,
        margin: "auto",
    };

    @styleRule
    label = {
        fontSize: 13,
        color: this.themeProps.LABEL,
        marginBottom: 18,
    };
}

@registerStyle(SocialLoginButtonsStyle)
export class SocialLoginButtons extends UI.Element {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            onButtonClick: NOOP_FUNCTION,
            onLoginCallback: NOOP_FUNCTION,
            socialApps: Object.keys(socialAppDescriptors), // TODO @cleanup rename this to externalAuthProviders and default to an explicit list
        };
    }

    getSocialApps() {
        return this.options.socialApps.filter(socialApp => socialApp !== SOCIAL_APPS.CDS);
    }

    render() {
        const {styleSheet} = this;
        const {onLoginCallback} = this.options;

        if (!this.getSocialApps().length) {
            return null;
        }

        return [
            <div className={styleSheet.buttons}>{this.getSocialButtons(onLoginCallback)}</div>,
        ];
    }

    getSocialButtons(loginSuccessCallback) {
        return this.getSocialApps().map(appName => {
            const appDescriptor = socialAppDescriptors[appName];
            const {icon, styleSheet} = socialAppDescriptors[appName];
            return (
                <SocialButton
                    testId={appName + "Button"}
                    clientName={appName}
                    styleSheet={styleSheet}
                    Icon={icon}
                    onClick={() => this.options.onButtonClick(appDescriptor)}
                    onLoginCallback={() => loginSuccessCallback()}
                />
            );
        });
    }
}
